import {
  BACKUP_STORAGE_KEY,
  PRIMARY_STORAGE_KEY,
  cloneState,
  createDefaultState,
  parseArcadeState,
  type ArcadeStateV1,
} from "./schema";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ArcadeStorage {
  readonly isPersistent: boolean;
  load(): ArcadeStateV1;
  snapshot(): ArcadeStateV1;
  mutate(mutator: (draft: ArcadeStateV1) => void): ArcadeStateV1;
}

const PROBE_KEY = "tesla-arcade:probe:v1";

function parseStored(raw: string | null): ArcadeStateV1 | undefined {
  if (raw === null) {
    return undefined;
  }

  try {
    return parseArcadeState(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

class LocalArcadeStorage implements ArcadeStorage {
  readonly #storage: StorageLike | undefined;
  readonly #now: () => number;
  #persistent = false;
  #loaded = false;
  #state = createDefaultState();

  constructor(storage: StorageLike | undefined, now: () => number) {
    this.#storage = storage;
    this.#now = now;
    this.#persistent = this.#probePersistence();
  }

  get isPersistent(): boolean {
    return this.#persistent;
  }

  load(): ArcadeStateV1 {
    if (this.#loaded) {
      return cloneState(this.#state);
    }
    this.#loaded = true;

    if (!this.#persistent || !this.#storage) {
      return cloneState(this.#state);
    }

    try {
      const primary = parseStored(this.#storage.getItem(PRIMARY_STORAGE_KEY));
      if (primary) {
        this.#state = primary;
        return cloneState(this.#state);
      }

      const backup = parseStored(this.#storage.getItem(BACKUP_STORAGE_KEY));
      if (backup) {
        this.#state = backup;
      }
    } catch {
      this.#persistent = false;
      this.#state = createDefaultState();
    }

    return cloneState(this.#state);
  }

  snapshot(): ArcadeStateV1 {
    if (!this.#loaded) {
      this.load();
    }
    return cloneState(this.#state);
  }

  mutate(mutator: (draft: ArcadeStateV1) => void): ArcadeStateV1 {
    if (!this.#loaded) {
      this.load();
    }

    const previous = cloneState(this.#state);
    const draft = cloneState(this.#state);
    mutator(draft);
    draft.writtenAt = this.#now();

    const validated = parseArcadeState(draft);
    if (!validated) {
      throw new Error("Refusing to persist invalid arcade state");
    }

    this.#state = validated;

    if (this.#persistent && this.#storage) {
      try {
        this.#storage.setItem(BACKUP_STORAGE_KEY, JSON.stringify(previous));
        this.#storage.setItem(PRIMARY_STORAGE_KEY, JSON.stringify(validated));
      } catch {
        this.#persistent = false;
      }
    }

    return cloneState(this.#state);
  }

  #probePersistence(): boolean {
    if (!this.#storage) {
      return false;
    }

    try {
      this.#storage.setItem(PROBE_KEY, "1");
      const roundTrip = this.#storage.getItem(PROBE_KEY) === "1";
      this.#storage.removeItem(PROBE_KEY);
      return roundTrip;
    } catch {
      try {
        this.#storage.removeItem(PROBE_KEY);
      } catch {
        // Persistence is already considered unavailable.
      }
      return false;
    }
  }
}

export function createStorageAdapter(
  storage: StorageLike | undefined,
  now: () => number = Date.now,
): ArcadeStorage {
  return new LocalArcadeStorage(storage, now);
}
